/**
 * Trade List Component
 *
 * Displays trades chronologically (newest first) without grouping.
 * Supports pagination with load more button.
 * NOW THEME-AWARE
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import type { Trade } from '../../services/portfolioApi';

interface TradeListProps {
  trades: Trade[];
  onTradePress?: (trade: Trade) => void;
  onEditPress?: (trade: Trade) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  totalCount?: number;
}

// Helper function to get transaction type label
function getTransactionTypeLabel(transactionType: string | undefined, t: any): string {
  switch (transactionType) {
    case 'buy':
      return t('portfolio.buy', { defaultValue: 'Buy' });
    case 'sell':
      return t('portfolio.sell', { defaultValue: 'Sell' });
    case 'deposit':
      return t('portfolio.deposit', { defaultValue: 'Deposit' });
    case 'withdrawal':
      return t('portfolio.withdrawal', { defaultValue: 'Withdrawal' });
    case 'split':
      return t('portfolio.split', { defaultValue: 'Split' });
    case 'consolidate':
      return t('portfolio.consolidate', { defaultValue: 'Consolidate' });
    case 'bonus':
      return t('portfolio.bonus', { defaultValue: 'Bonus' });
    default:
      return transactionType || t('portfolio.buy', { defaultValue: 'Buy' });
  }
}

// Helper function to get transaction type color
function getTransactionTypeColor(transactionType: string | undefined, theme: any): string {
  switch (transactionType) {
    case 'buy':
    case 'deposit':
      return theme.colors.success;
    case 'sell':
    case 'withdrawal':
      return theme.colors.error;
    case 'split':
    case 'consolidate':
    case 'bonus':
      return theme.colors.primary;
    default:
      return theme.colors.textSecondary;
  }
}

export function TradeList({
  trades,
  onTradePress,
  onEditPress,
  onLoadMore,
  hasMore = false,
  loading = false,
  totalCount,
}: TradeListProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Sort trades by datetime descending (newest first)
  const sortedTrades = useMemo(() => {
    return [...trades].sort(
      (a, b) => new Date(b.trade_datetime).getTime() - new Date(a.trade_datetime).getTime()
    );
  }, [trades]);

  const renderTradeItem = useCallback(
    ({ item }: { item: Trade }) => {
      const tradeDate = new Date(item.trade_datetime);
      const quantity = parseFloat(item.quantity);
      const transactionTypeLabel = getTransactionTypeLabel(item.transaction_type, t);
      const transactionTypeColor = getTransactionTypeColor(item.transaction_type, theme);
      const hasComment = item.comment && item.comment.trim().length > 0;

      return (
        <TouchableOpacity
          style={[
            styles.tradeItem,
            { backgroundColor: theme.colors.backgroundElevated, borderColor: theme.colors.border },
          ]}
          onPress={() => onTradePress?.(item)}
          activeOpacity={0.7}
        >
          <View style={styles.tradeHeader}>
            <View style={styles.tradeHeaderLeft}>
              <Text style={[styles.symbolText, { color: theme.colors.text }]}>{item.symbol}</Text>
              <View
                style={[
                  styles.transactionTypeBadge,
                  { backgroundColor: transactionTypeColor + '20' },
                ]}
              >
                <Text style={[styles.transactionTypeText, { color: transactionTypeColor }]}>
                  {transactionTypeLabel}
                </Text>
              </View>
            </View>
            <View style={styles.tradeHeaderRight}>
              <Text style={[styles.tradeDateText, { color: theme.colors.textTertiary }]}>
                {tradeDate.toLocaleDateString()}
              </Text>
              {onEditPress && (
                <TouchableOpacity
                  style={[styles.editButton, { borderColor: theme.colors.border }]}
                  onPress={() => onEditPress(item)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.editButtonText, { color: theme.colors.primary }]}>
                    {t('common.edit', { defaultValue: 'Edit' })}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          <View style={styles.tradeDetails}>
            <View style={styles.tradeDetailRow}>
              <Text style={[styles.tradeDetailLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.quantity', { defaultValue: 'Quantity' })}
              </Text>
              <Text
                style={[
                  styles.tradeDetailValue,
                  { color: quantity >= 0 ? theme.colors.success : theme.colors.error },
                ]}
              >
                {quantity >= 0 ? '+' : ''}
                {quantity}
              </Text>
            </View>

            <View style={styles.tradeDetailRow}>
              <Text style={[styles.tradeDetailLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.price', { defaultValue: 'Price' })}
              </Text>
              <Text style={[styles.tradeDetailValue, { color: theme.colors.text }]}>
                {item.currency} {parseFloat(item.trade_price).toFixed(2)}
              </Text>
            </View>

            <View style={styles.tradeDetailRow}>
              <Text style={[styles.tradeDetailLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.realizedPL', { defaultValue: 'Realized P/L' })}
              </Text>
              <Text
                style={[
                  styles.tradeDetailValue,
                  parseFloat(item.realized_pl) >= 0
                    ? { color: theme.colors.success }
                    : { color: theme.colors.error },
                ]}
              >
                {item.currency} {parseFloat(item.realized_pl) >= 0 ? '+' : ''}
                {parseFloat(item.realized_pl).toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Show comment if exists */}
          {hasComment && (
            <View style={styles.commentSection}>
              <Text style={[styles.commentLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.comment', { defaultValue: 'Note' })}:
              </Text>
              <Text style={[styles.commentText, { color: theme.colors.text }]} numberOfLines={2}>
                {item.comment}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      );
    },
    [theme, onTradePress, onEditPress, t]
  );

  const renderListEmptyComponent = useCallback(() => {
    if (loading) {
      return null;
    }
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyText, { color: theme.colors.text }]}>
          {t('portfolio.noTradesFound', { defaultValue: 'No trades found' })}
        </Text>
        <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
          {t('portfolio.addFirstTrade', { defaultValue: 'Add your first trade to get started' })}
        </Text>
      </View>
    );
  }, [loading, theme, t]);

  const renderListFooter = useCallback(() => {
    if (!hasMore && trades.length > 0) {
      return (
        <View style={styles.listFooter}>
          <Text style={[styles.footerText, { color: theme.colors.textTertiary }]}>
            {totalCount !== undefined
              ? t('portfolio.allTradesLoaded', {
                  count: totalCount,
                  defaultValue: `All ${totalCount} trades loaded`,
                })
              : t('portfolio.allTradesLoaded', { defaultValue: 'All trades loaded' })}
          </Text>
        </View>
      );
    }

    return null;
  }, [hasMore, trades.length, totalCount, theme, t]);

  if (trades.length === 0 && !loading) {
    return renderListEmptyComponent();
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={sortedTrades}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderTradeItem}
        ListEmptyComponent={renderListEmptyComponent}
        ListFooterComponent={renderListFooter}
        contentContainerStyle={styles.listContent}
        onEndReached={() => {
          if (hasMore && !loading && onLoadMore) {
            onLoadMore();
          }
        }}
        onEndReachedThreshold={0.5}
      />

      {/* Manual Load More Button */}
      {hasMore && trades.length > 0 && !loading && onLoadMore && (
        <View
          style={[
            styles.loadMoreContainer,
            { backgroundColor: theme.colors.surface, borderTopColor: theme.colors.border },
          ]}
        >
          <TouchableOpacity
            style={[
              styles.loadMoreButton,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
              },
            ]}
            onPress={onLoadMore}
          >
            <Text style={[styles.loadMoreButtonText, { color: theme.colors.primary }]}>
              {t('portfolio.loadMore', { defaultValue: 'Load More' })}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Loading indicator at bottom */}
      {loading && trades.length > 0 && (
        <View style={styles.listFooter}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Text style={[styles.footerText, { color: theme.colors.textSecondary }]}>
            {t('portfolio.loadingMore', { defaultValue: 'Loading more...' })}
          </Text>
        </View>
      )}
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    listContent: {
      padding: 16,
      paddingTop: 8,
      paddingBottom: 8,
      flexGrow: 1,
    },
    tradeItem: {
      padding: 14,
      borderRadius: theme.radii.md,
      marginBottom: 12,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    tradeHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    tradeHeaderLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flex: 1,
    },
    tradeHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    symbolText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    transactionTypeBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: theme.radii.sm,
    },
    transactionTypeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: 'uppercase',
    },
    tradeDateText: {
      fontSize: theme.typography.fontSizeXs,
    },
    tradeDetails: {
      gap: 8,
    },
    tradeDetailRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    tradeDetailLabel: {
      fontSize: theme.typography.fontSizeSm,
    },
    tradeDetailValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    commentSection: {
      marginTop: 8,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    commentLabel: {
      fontSize: theme.typography.fontSizeXs,
      marginBottom: 4,
    },
    commentText: {
      fontSize: theme.typography.fontSizeSm,
      fontStyle: 'italic',
    },
    editButton: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: theme.radii.sm,
      borderWidth: 1,
    },
    editButtonText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
      marginTop: 100,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
    },
    emptySubtext: {
      fontSize: theme.typography.fontSizeSm,
    },
    listFooter: {
      paddingVertical: 24,
      alignItems: 'center',
      gap: 8,
    },
    footerText: {
      fontSize: theme.typography.fontSizeSm,
    },
    loadMoreContainer: {
      padding: theme.spacing.md,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    loadMoreButton: {
      paddingVertical: theme.spacing.md,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    loadMoreButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
